// ============================================
// ComputePass - GPU Compute Abstraction
// ============================================

import { type IRenderContext } from './IRenderContext';
import {
  type ComputePipelineHandle,
  type ComputeBindGroup,
  type DispatchSize,
  type BufferHandle,
  type TextureHandle,
  type ShaderHandle,
  type ComputePipelineDescriptor,
  NULL_HANDLE,
} from '../types';

export interface ComputePassOptions {
  /** Debug label */
  label?: string;
}

/**
 * ComputePass - High-level abstraction for GPU compute operations
 * 
 * Used for VFX simulation, particle systems, procedural generation, etc.
 * 
 * @example
 * ```ts
 * const particleUpdate = new ComputePass(ctx, {
 *   label: 'particle-update'
 * });
 * 
 * particleUpdate.setPipeline(updatePipeline);
 * particleUpdate.bind({
 *   storageBuffers: [
 *     { buffer: particleBuffer },
 *   ],
 *   uniforms: [uniformBuffer],
 * });
 * particleUpdate.dispatch({ x: Math.ceil(particleCount / 64) });
 * particleUpdate.barrier();
 * ```
 */
export class ComputePass {
  private ctx: IRenderContext;
  private label: string;
  private currentPipeline: ComputePipelineHandle = NULL_HANDLE;
  
  constructor(ctx: IRenderContext, options: ComputePassOptions = {}) {
    this.ctx = ctx;
    this.label = options.label ?? 'compute-pass';
    
    if (!ctx.supportsCompute) {
      console.warn(`[ComputePass:${this.label}] Compute shaders not supported on this backend`);
    }
  }
  
  /**
   * Check if compute is available
   */
  get isSupported(): boolean {
    return this.ctx.supportsCompute;
  }
  
  /**
   * Set the compute pipeline
   */
  setPipeline(handle: ComputePipelineHandle): this {
    if (!this.ctx.setComputePipeline) {
      throw new Error('Compute not supported');
    }
    this.ctx.setComputePipeline(handle);
    this.currentPipeline = handle;
    return this;
  }
  
  /**
   * Bind resources (storage buffers, textures, uniforms)
   */
  bind(bindings: ComputeBindGroup): this {
    if (!this.ctx.bindComputeResources) {
      throw new Error('Compute not supported');
    }
    this.ctx.bindComputeResources(bindings);
    return this;
  }
  
  /**
   * Dispatch compute workgroups
   */
  dispatch(size: DispatchSize): this {
    if (!this.ctx.dispatchCompute) {
      throw new Error('Compute not supported');
    }
    this.ctx.dispatchCompute(size);
    return this;
  }
  
  /**
   * Dispatch compute workgroups based on element count and workgroup size
   * Automatically calculates the number of workgroups needed
   */
  dispatchForCount(count: number, workgroupSize = 64): this {
    const numWorkgroups = Math.ceil(count / workgroupSize);
    return this.dispatch({ x: numWorkgroups });
  }
  
  /**
   * Dispatch with indirect buffer
   */
  dispatchIndirect(buffer: BufferHandle, offset = 0): this {
    if (!this.ctx.dispatchComputeIndirect) {
      throw new Error('Compute indirect not supported');
    }
    this.ctx.dispatchComputeIndirect(buffer, offset);
    return this;
  }
  
  /**
   * Insert memory barrier to ensure compute writes are visible
   */
  barrier(): this {
    if (this.ctx.computeBarrier) {
      this.ctx.computeBarrier();
    }
    return this;
  }
}

// ============================================
// ComputeBuffer - Storage Buffer Wrapper
// ============================================

export interface ComputeBufferOptions {
  /** Number of elements */
  count: number;
  /** Size of each element in bytes */
  stride: number;
  /** Initial data (optional) */
  data?: ArrayBuffer | ArrayBufferView;
  /** Allow reading from GPU (slower) */
  readable?: boolean;
  /** Debug label */
  label?: string;
}

/**
 * ComputeBuffer - High-level wrapper for GPU storage buffers
 * 
 * @example
 * ```ts
 * // Create particle buffer
 * const particles = new ComputeBuffer(ctx, {
 *   count: 10000,
 *   stride: 32, // ParticleData size
 *   label: 'particles'
 * });
 * 
 * // Use in compute pass
 * pass.bind({ storageBuffers: [{ buffer: particles.handle }] });
 * 
 * // Later: read back data (if readable)
 * const data = await particles.read();
 * ```
 */
export class ComputeBuffer {
  private ctx: IRenderContext;
  private _handle: BufferHandle;
  private _count: number;
  private _stride: number;
  private _readable: boolean;
  
  constructor(ctx: IRenderContext, options: ComputeBufferOptions) {
    this.ctx = ctx;
    this._count = options.count;
    this._stride = options.stride;
    this._readable = options.readable ?? false;
    
    const usage: import('../types').BufferUsage[] = ['storage'];
    if (this._readable) {
      usage.push('copy-src');
    }
    if (options.data) {
      usage.push('copy-dst');
    }
    
    this._handle = ctx.createBuffer({
      size: options.count * options.stride,
      usage,
      data: options.data,
      label: options.label ?? "PewPew Unknown Pass",
    });
  }
  
  get handle(): BufferHandle {
    return this._handle;
  }
  
  get count(): number {
    return this._count;
  }
  
  get stride(): number {
    return this._stride;
  }
  
  get byteLength(): number {
    return this._count * this._stride;
  }
  
  /**
   * Update buffer data
   */
  write(data: ArrayBufferView, offset = 0): void {
    this.ctx.updateBuffer(this._handle, data, offset);
  }
  
  /**
   * Create binding descriptor for use in ComputeBindGroup
   */
  binding(offset = 0, size?: number): import('../types').StorageBufferBinding {
    return {
      buffer: this._handle,
      offset,
      size: size ?? this.byteLength,
    };
  }
  
  /**
   * Destroy the buffer
   */
  destroy(): void {
    this.ctx.destroyBuffer(this._handle);
  }
}

// ============================================
// ComputeTexture - Storage Texture Wrapper
// ============================================

export interface ComputeTextureOptions {
  width: number;
  height: number;
  /** Format (default: rgba32float for compute) */
  format?: import('../types').TextureFormat;
  /** Debug label */
  label?: string;
}

/**
 * ComputeTexture - Storage texture for compute read/write
 * 
 * @example
 * ```ts
 * // Create noise texture
 * const noise = new ComputeTexture(ctx, {
 *   width: 512,
 *   height: 512,
 *   label: 'noise'
 * });
 * 
 * // Write from compute shader
 * pass.bind({ storageTextures: [{ texture: noise.handle, access: 'write-only' }] });
 * pass.dispatch({ x: 512/8, y: 512/8 }); // 8x8 workgroup
 * 
 * // Use as regular texture for rendering
 * batch.draw(sprite, noise.handle);
 * ```
 */
export class ComputeTexture {
  private ctx: IRenderContext;
  private _handle: TextureHandle;
  private _width: number;
  private _height: number;
  
  constructor(ctx: IRenderContext, options: ComputeTextureOptions) {
    this.ctx = ctx;
    this._width = options.width;
    this._height = options.height;
    
    this._handle = ctx.createTexture({
      width: options.width,
      height: options.height,
      format: options.format ?? 'rgba32float',
      usage: ['storage', 'sampled'],
      label: options.label,
    });
  }
  
  get handle(): TextureHandle {
    return this._handle;
  }
  
  get width(): number {
    return this._width;
  }
  
  get height(): number {
    return this._height;
  }
  
  /**
   * Create binding for write access
   */
  writeBinding(): import('../types').StorageTextureBinding {
    return { texture: this._handle, access: 'write-only' };
  }
  
  /**
   * Create binding for read access
   */
  readBinding(): import('../types').StorageTextureBinding {
    return { texture: this._handle, access: 'read-only' };
  }
  
  /**
   * Create binding for read-write access
   */
  readWriteBinding(): import('../types').StorageTextureBinding {
    return { texture: this._handle, access: 'read-write' };
  }
  
  /**
   * Resize the texture (destroys old data)
   */
  resize(width: number, height: number): void {
    if (width === this._width && height === this._height) return;
    
    this.ctx.destroyTexture(this._handle);
    this._width = width;
    this._height = height;
    
    this._handle = this.ctx.createTexture({
      width,
      height,
      format: 'rgba32float',
      usage: ['storage', 'sampled'],
    });
  }
  
  /**
   * Destroy the texture
   */
  destroy(): void {
    this.ctx.destroyTexture(this._handle);
  }
}

// ============================================
// Compute Pipeline Builder
// ============================================

/**
 * Builder for compute pipelines with shader graph support in mind
 */
export class ComputePipelineBuilder {
  private ctx: IRenderContext;
  private source: string = '';
  private entryPoint: string = 'main';
  private workgroupSize: [number, number, number] = [64, 1, 1];
  private label: string = 'compute-pipeline';
  
  constructor(ctx: IRenderContext) {
    this.ctx = ctx;
  }
  
  /**
   * Set shader source
   */
  shader(source: string): this {
    this.source = source;
    return this;
  }
  
  /**
   * Set entry point name
   */
  entry(name: string): this {
    this.entryPoint = name;
    return this;
  }
  
  /**
   * Set workgroup size
   */
  workgroup(x: number, y = 1, z = 1): this {
    this.workgroupSize = [x, y, z];
    return this;
  }
  
  /**
   * Set debug label
   */
  name(label: string): this {
    this.label = label;
    return this;
  }
  
  /**
   * Build the pipeline
   */
  build(): { shader: ShaderHandle; pipeline: ComputePipelineHandle } | null {
    if (!this.ctx.createComputeShader || !this.ctx.createComputePipeline) {
      console.warn('[ComputePipelineBuilder] Compute not supported');
      return null;
    }
    
    const shader = this.ctx.createComputeShader(this.source);
    const pipeline = this.ctx.createComputePipeline({
      shader,
      entryPoint: this.entryPoint,
      workgroupSize: this.workgroupSize,
      label: this.label,
    });
    
    return { shader, pipeline };
  }
}
